import { Account, getExternalPlatformIdFromInternalPlatformId, getInternalPlatformIdFromExternalPlatformId, validateAndFormatAddress, 
// RPC types
ORBY_CREATE_ACCOUNT_CLUSTER, ORBY_GET_VIRTUAL_NODE_RPC_URLS_FOR_SUPPORTED_PLATFORMS, ORBY_ENABLE_PLATFORM_ABSTRACTION_FOR_ACCOUNT_CLUSTER, ORBY_SET_CUSTOM_PLATFORM_ENDPOINTS_FOR_ACCOUNT_CLUSTER, ORBY_REMOVE_CUSTOM_PLATFORM_ENDPOINTS_FOR_ACCOUNT_CLUSTER, ORBY_GET_CUSTOM_PLATFORM_ENDPOINTS_FOR_ACCOUNT_CLUSTER, ORBY_IS_PLATFORM_SUPPORTED_ON_ACCOUNT_CLUSTER, ORBY_GET_NODE_RPC_URL, ORBY_GET_VIRTUAL_NODE_RPC_URL, ORBY_GET_ACTIVITY, ORBY_GET_STABLECOIN_AUTO_DEPOSIT_SETTINGS_FOR_ACCOUNT, ORBY_VERIFY_ACCOUNTS, } from "@orb-labs/types";
import { extractAccountCluster, extractActivities, extractStablecoinAutoDepositSettingsForAccount, } from "../utils/action_helpers.js";
import { LibraryRequest } from "../actions/library_request.js";
export class AccountClusterActions extends LibraryRequest {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(library, client, provider) {
        super(library, client, provider);
    }
    async createAccountCluster(accounts, authentication) {
        const formattedAccounts = accounts.map((account) => account.toAccountModel());
        const accountCluster = await this.sendRequest(ORBY_CREATE_ACCOUNT_CLUSTER, [
            {
                accounts: formattedAccounts,
                authentication,
            },
        ]);
        return extractAccountCluster(accountCluster);
    }
    async getVirtualNodeRpcUrlsForSupportedPlatforms(accountClusterId) {
        const { virtualNodeRpcUrlsForSupportedPlatforms, message, code } = await this.sendRequest(ORBY_GET_VIRTUAL_NODE_RPC_URLS_FOR_SUPPORTED_PLATFORMS, [{ accountClusterId }]);
        if (code && message) {
            console.error("[getVirtualNodeRpcUrlsForSupportedPlatforms]", code, message);
            return undefined;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return virtualNodeRpcUrlsForSupportedPlatforms.map((info) => ({
            platformId: getInternalPlatformIdFromExternalPlatformId(info.platformId),
            virtualNodeRpcUrl: info.virtualNodeRpcUrl,
            entrypointAccountAddress: validateAndFormatAddress(info?.entrypointAccountAddress),
        }));
    }
    async enablePlatformAbstractionForAccountCluster(accountClusterId, enable) {
        const { success, message, code } = await this.sendRequest(ORBY_ENABLE_PLATFORM_ABSTRACTION_FOR_ACCOUNT_CLUSTER, [{ accountClusterId, enable }]);
        if (code && message) {
            console.error("[enablePlatformAbstractionForAccountCluster]", code, message);
            return undefined;
        }
        return success;
    }
    async setCustomPlatformEndpointsForAccountCluster(accountClusterId, chainEndpoints) {
        const promises = chainEndpoints.map(async (endpoint) => ({
            platformId: getExternalPlatformIdFromInternalPlatformId(endpoint.platformId),
            customRpcUrls: endpoint.customRpcUrls,
            customIndexerUrls: endpoint.customIndexerUrls,
        }));
        const formattedEndpoints = await Promise.all(promises);
        const { success, message, code } = await this.sendRequest(ORBY_SET_CUSTOM_PLATFORM_ENDPOINTS_FOR_ACCOUNT_CLUSTER, [{ accountClusterId, chainEndpoints: formattedEndpoints }]);
        if (code && message) {
            console.error("[setCustomPlatformEndpointsForAccountCluster]", code, message);
            return undefined;
        }
        return success;
    }
    async removeCustomPlatformEndpointsForAccountCluster(accountClusterId, platformIds) {
        const promises = platformIds.map(async (platformId) => getExternalPlatformIdFromInternalPlatformId(platformId));
        const formattedPlatformIds = await Promise.all(promises);
        const { success, message, code } = await this.sendRequest(ORBY_REMOVE_CUSTOM_PLATFORM_ENDPOINTS_FOR_ACCOUNT_CLUSTER, [{ accountClusterId, platformIds: formattedPlatformIds }]);
        if (code && message) {
            console.error("[removeCustomPlatformEndpointsForAccountCluster]", code, message);
            return undefined;
        }
        return success;
    }
    async getCustomPlatformEndpointsForAccountCluster(accountClusterId, returnPlatformIdsOnly) {
        const { chainEndpoints, message, code } = await this.sendRequest(ORBY_GET_CUSTOM_PLATFORM_ENDPOINTS_FOR_ACCOUNT_CLUSTER, [{ accountClusterId, returnPlatformIdsOnly }]);
        if (code && message) {
            console.error("[getCustomPlatformEndpointsForAccountCluster]", code, message);
            return undefined;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return chainEndpoints.map((endpoint) => ({
            platformId: getInternalPlatformIdFromExternalPlatformId(endpoint.platformId),
            customRpcUrls: endpoint.customRpcUrls,
            customIndexerUrls: endpoint.customIndexerUrls,
        }));
    }
    async isPlatformSupportedOnAccountCluster(accountClusterId, platformId) {
        const orbyPlatformId = getExternalPlatformIdFromInternalPlatformId(platformId);
        const { supportStatus, message, code } = await this.sendRequest(ORBY_IS_PLATFORM_SUPPORTED_ON_ACCOUNT_CLUSTER, [{ accountClusterId, platformId: orbyPlatformId }]);
        if (code && message) {
            console.error("[isPlatformSupportedOnAccountCluster]", code, message);
            return undefined;
        }
        return supportStatus;
    }
    async getNodeRpcUrl(accountClusterId, platformId) {
        const orbyPlatformId = getExternalPlatformIdFromInternalPlatformId(platformId);
        const { nodeRpcUrl, code, message } = await this.sendRequest(ORBY_GET_NODE_RPC_URL, [
            { accountClusterId, platformId: orbyPlatformId },
        ]);
        if (code && message) {
            console.error("[getNodeRpcUrl]", code, message);
            return undefined;
        }
        return nodeRpcUrl;
    }
    async getVirtualNodeRpcUrl(accountClusterId, platformId, entrypointAccountAddress) {
        const orbyPlatformId = getExternalPlatformIdFromInternalPlatformId(platformId);
        const { virtualNodeRpcUrl, message, code } = await this.sendRequest(ORBY_GET_VIRTUAL_NODE_RPC_URL, [{ accountClusterId, platformId: orbyPlatformId, entrypointAccountAddress }]);
        if (code && message) {
            console.error("[getVirtualNodeRpcUrl]", code, message);
            return undefined;
        }
        return virtualNodeRpcUrl;
    }
    async getActivity(accountClusterId, limit, offset, order, startDate, endDate, filters) {
        const { pageInfo, edges, code, message } = await this.sendRequest(ORBY_GET_ACTIVITY, [
            { accountClusterId, limit, offset, order, startDate, endDate, filters },
        ]);
        if (code && message) {
            console.error("[getActivity]", code, message);
            return undefined;
        }
        return { activities: extractActivities(edges), pageInfo };
    }
    async getStablecoinAutoDepositSettingsForAccount(accountClusterId, account) {
        const { settings, message, code } = await this.sendRequest(ORBY_GET_STABLECOIN_AUTO_DEPOSIT_SETTINGS_FOR_ACCOUNT, [{ accountClusterId, account: account?.toAccountModel() }]);
        if (code && message) {
            console.error("[getStablecoinAutoDepositSettingsForAccountCluster]", code, message);
            return undefined;
        }
        return extractStablecoinAutoDepositSettingsForAccount(settings);
    }
    async verifyAccounts(accounts) {
        const promises = accounts?.map(async (account) => ({
            externalId: account.externalId,
            refreshToken: account.refreshToken,
            accessToken: account.accessToken,
            accessTokenExpiresInSeconds: account.accessTokenExpiresInSeconds,
            refreshTokenExpiresInSeconds: account.refreshTokenExpiresInSeconds,
            platformName: account.platformName,
            platformId: getExternalPlatformIdFromInternalPlatformId(account.platformId),
            provider: account.provider,
        }));
        const formattedAccounts = await Promise.all(promises);
        const { accounts: verifiedAccounts, message, code, } = await this.sendRequest(ORBY_VERIFY_ACCOUNTS, [{ accountDetails: formattedAccounts }]);
        if (code && message) {
            console.error("[getStablecoinAutoDepositSettingsForAccountCluster]", code, message);
            return undefined;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return verifiedAccounts?.map((account) => Account.toAccount(account));
    }
}
